import { faker } from '@faker-js/faker';
import type { 
  SupportTicket, 
  SupportTicketCategory, 
  SupportTicketPriority, 
  SupportTicketStatus,
  KnowledgeBaseArticle,
  ServiceRequest 
} from '../types';

const supportTicketCategories: SupportTicketCategory[] = [
  'technical_issue',
  'billing',
  'account_management',
  'product_inquiry',
  'training',
  'documentation',
  'other'
];

const priorities: SupportTicketPriority[] = ['low', 'medium', 'high', 'critical'];

export function generateSupportTicket(): SupportTicket {
  const createdAt = faker.date.past({ years: 1 });
  const updatedAt = faker.date.between({ from: createdAt, to: new Date() });
  const resolvedAt = faker.helpers.maybe(() => 
    faker.date.between({ from: updatedAt, to: new Date() }),
    { probability: 0.7 }
  ) || null;

  const responseTime = faker.number.float({ min: 0.5, max: 24, multipleOf: 0.5 });
  const resolutionTime = resolvedAt ? 
    faker.number.float({ min: 1, max: 72, multipleOf: 0.5 }) : null;

  return {
    id: faker.string.uuid(),
    ticketNumber: `TICKET-${faker.string.numeric(6)}`,
    userId: faker.string.uuid(),
    companyId: faker.string.uuid(),
    title: faker.lorem.sentence(),
    description: faker.lorem.paragraphs(2),
    category: faker.helpers.arrayElement(supportTicketCategories),
    status: resolvedAt ? 'resolved' : faker.helpers.arrayElement(['open', 'in_progress']),
    priority: faker.helpers.arrayElement(priorities),
    createdAt,
    updatedAt,
    resolvedAt,
    assignedTo: faker.helpers.maybe(() => faker.person.fullName(), { probability: 0.8 }) || null,
    responseTime,
    resolutionTime
  };
}

export function generateKnowledgeBaseArticle(): KnowledgeBaseArticle {
  return {
    id: faker.string.uuid(),
    title: faker.lorem.sentence(),
    category: faker.helpers.arrayElement(supportTicketCategories),
    content: faker.lorem.paragraphs(5),
    views: faker.number.int({ min: 0, max: 1000 }),
    lastUpdated: faker.date.past({ years: 1 }),
    tags: Array.from({ length: faker.number.int({ min: 1, max: 5 }) }, () => 
      faker.lorem.word()
    )
  };
}

export function generateServiceRequest(): ServiceRequest {
  const requestedDate = faker.date.past({ years: 1 });
  const scheduledDate = faker.helpers.maybe(() => 
    faker.date.between({ from: requestedDate, to: new Date() }),
    { probability: 0.8 }
  ) || null;
  const completedDate = scheduledDate ? 
    faker.helpers.maybe(() => 
      faker.date.between({ from: scheduledDate, to: new Date() }),
      { probability: 0.6 }
    ) || null : null;

  // Determine status based on dates
  let status: SupportTicketStatus;
  if (completedDate) {
    status = 'resolved';
  } else if (scheduledDate) {
    status = 'in_progress';
  } else {
    status = 'open';
  }

  return {
    id: faker.string.uuid(),
    requestNumber: `SR-${faker.string.numeric(6)}`,
    userId: faker.string.uuid(),
    companyId: faker.string.uuid(),
    type: faker.helpers.arrayElement(['training', 'consultation', 'maintenance', 'other']),
    title: faker.lorem.sentence(),
    status,
    requestedDate,
    scheduledDate,
    completedDate,
    description: faker.lorem.paragraph(),
    assignedTo: faker.helpers.maybe(() => faker.person.fullName(), { probability: 0.7 }) || null
  };
}

export function generateSupportTickets(count: number): SupportTicket[] {
  return Array.from({ length: count }, generateSupportTicket);
}

export function generateKnowledgeBaseArticles(count: number): KnowledgeBaseArticle[] {
  return Array.from({ length: count }, generateKnowledgeBaseArticle);
}

export function generateServiceRequests(count: number): ServiceRequest[] {
  return Array.from({ length: count }, generateServiceRequest);
} 